/* eslint-disable no-confusing-arrow,implicit-arrow-linebreak */
import _ from 'lodash';
import { Action, Dispatch } from 'redux';
import { IAppState } from './reducers';
import {
  ITag, getAllTags, createTag, updateTag, deleteTag, ITagCreate, ITagPut, getTagsByOrgId, ITagAsset, addTagToAsset, removeTagFromAsset, getTagById,
} from '../api/vault/tags';
import { selectUserOrgIds } from './user/userOrgRole';
import { IOrgTagIds } from '../api/vault/searchEngine';
import {
  queryGamesAction, updateSearchHitAddTag, updateSearchHitRemoveTag, updateSearchHitTags,
} from './search';

export enum ActionType {
  AddTag = 'Tags.AddTag',
  UpdateTag = 'Tags.UpdateTag',
  DeleteTag = 'Tags.DeleteTag',
  OrgTagsLoading = 'Tags.OrgTagsLoading',
  LoadingAdminTags = 'Tags.LoadingAdminTags',
  OrgTagsLoaded = 'Tags.OrgTagsLoaded',
  AdminTagsLoaded = 'Tags.AdminTagsLoaded',
  TagAssignedToAsset = 'Tags.TagAssignedToAsset',
  TagRemovedFromAsset = 'Tags.TagRemovedFromAsset',
  SetCurrentAssetId = 'Tags.SetCurrentAssetId',
  GetCurrentAssetId = 'Tags.GetCurrentAssetId',
  CurrentAssetTagsLoaded = 'Tags.CurrentAssetTagsLoaded',
  CurrentIndeterminateTagsLoaded = 'Tags.CurrentIndeterminateTagsLoaded',
  UpdateCurrentAssetTags = 'Tags.UpdateCurrentAssetTags',
  UpdateCurrentIndeterminateTags = 'Tags.UpdateCurrentIndeterminateTags',
  UpdateTotalAssets = 'Tags.UpdateTotalAssets',
}

export enum UpdateSearchHitType {
  AddTag = 'UpdateSearchHitAddTag',
  RemoveTag = 'UpdateSearchHitRemoveTag',
  EditTag = 'UpdateSearchHitEditTag',
  DeleteTag = 'UpdateSearchHitDeleteTag',
}

export interface ITagsAction extends Action<ActionType> {
  payload?: any;
}

export interface IState {
  orgTagsLoaded: boolean;
  orgTags: Array<ITag>;
  isAdminTagsLoaded: boolean;
  adminTags: Array<ITag>;
  currentAssetId: string;
  currentAssetTags: Array<ITag>;
  currentIndeterminateTags: Array<ITag>;
  currentAssetTagsLoaded: boolean;
}

const INITIAL_STATE: IState = {
  orgTagsLoaded: false,
  orgTags: [],
  isAdminTagsLoaded: false,
  adminTags: [],
  currentAssetId: '',
  currentAssetTags: [],
  currentIndeterminateTags: [],
  currentAssetTagsLoaded: false,
};

export default function reducer(state: IState = INITIAL_STATE, action: ITagsAction): IState {
  switch (action.type) {
    case ActionType.AddTag:
      return {
        ...state,
        orgTagsLoaded: false,
      };
    case ActionType.UpdateTag:
      return {
        ...state,
        orgTagsLoaded: false,
      };
    case ActionType.DeleteTag:
      return {
        ...state,
        orgTagsLoaded: false,
      };
    case ActionType.OrgTagsLoading:
      return {
        ...state,
        orgTagsLoaded: false,
      };
    case ActionType.LoadingAdminTags:
      return {
        ...state,
        isAdminTagsLoaded: false,
      };
    case ActionType.OrgTagsLoaded:
      return {
        ...state,
        orgTags: action.payload.tags,
        orgTagsLoaded: true,
      };
    case ActionType.AdminTagsLoaded:
      return {
        ...state,
        adminTags: action.payload.tags,
        isAdminTagsLoaded: true,
      };
    case ActionType.TagAssignedToAsset:
      return {
        ...state,
      };
    case ActionType.TagRemovedFromAsset:
      return {
        ...state,
      };
    case ActionType.SetCurrentAssetId:
      return {
        ...state,
        currentAssetId: action.payload.assetId,
      };
    case ActionType.CurrentAssetTagsLoaded:
      return {
        ...state,
        currentAssetTags: action.payload.currentAssetTags,
        currentAssetTagsLoaded: true,
      };
    case ActionType.CurrentIndeterminateTagsLoaded:
      return {
        ...state,
        currentIndeterminateTags: action.payload.currentIndeterminateTags,
      };
    case ActionType.UpdateCurrentAssetTags:
      return {
        ...state,
        currentAssetTags: action.payload.currentAssetTags,
        currentAssetTagsLoaded: true,
      };
    case ActionType.UpdateCurrentIndeterminateTags:
      return {
        ...state,
        currentIndeterminateTags: action.payload.currentIndeterminateTags,
      };
    case ActionType.UpdateTotalAssets:
      return {
        ...state,
        orgTags: action.payload.orgTags,
        orgTagsLoaded: true,
      };
    default:
      return state;
  }
}

export const loadAdminTags = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const response = await getAllTags();
    const tags = response.data.data;

    dispatch({
      type: ActionType.LoadingAdminTags,
    });

    dispatch({
      type: ActionType.AdminTagsLoaded,
      payload: {
        tags,
      },
    });
  } catch (e) {
    console.error('[QA] could not load admin tag data', e.toString());
  }
};

export const loadOrgTags = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const state = getState();
    const userOrgIds = selectUserOrgIds(state);
    const orgId = userOrgIds[0];

    if (userOrgIds.length > 1) {
      console.warn(`[QA] User belongs to multiple orgs -> targeting "${orgId}"`);
    }
    dispatch({ type: ActionType.OrgTagsLoading });

    const response = await getTagsByOrgId(orgId);
    const tags = response.data.data;

    dispatch({
      type: ActionType.OrgTagsLoaded,
      payload: {
        tags,
      },
    });
  } catch (e) {
    console.error('[QA] could not load org tag data', e.toString());
  }
};

export const loadAssetTags = (assetTags: IOrgTagIds[]) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const state = getState();
    const orgTags = [...state.tags.orgTags];
    const tags = [...assetTags];
    let currentAssetTags: ITag[];
    if (assetTags.length > 0) {
      currentAssetTags = tags.map((assetTag) => ({ ...assetTag, ...orgTags.find((orgTag) => orgTag._id === assetTag._id) }));
    }
    dispatch({
      type: ActionType.CurrentAssetTagsLoaded,
      payload: {
        currentAssetTags,
      },
    });
  } catch (e) {
    console.error('[QA] could not load current asset tags', e.toString());
  }
};

export const loadIndeterminateTags = (indeterminateTags: IOrgTagIds[]) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const state = getState();
    const orgTags = [...state.tags.orgTags];
    const tags = [...indeterminateTags];
    let currentIndeterminateTags: ITag[];
    if (indeterminateTags.length > 0) {
      currentIndeterminateTags = tags.map((indeterminateTag) => ({ ...indeterminateTag, ...orgTags.find((orgTag) => orgTag._id === indeterminateTag._id) }));
    }
    dispatch({
      type: ActionType.CurrentIndeterminateTagsLoaded,
      payload: {
        currentIndeterminateTags,
      },
    });
  } catch (e) {
    console.error('[QA] could not load current indeterminate tags', e.toString());
  }
};

export const updateAssetTags = (type: UpdateSearchHitType, assetId: string[], assetTags: ITag[], tag?: ITag) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const state = getState();
    const orgTags = [...state.tags.orgTags];
    const tags = [...assetTags];
    let currentAssetTags: ITag[];
    if (assetTags.length > 0) {
      currentAssetTags = tags.map((assetTag) => ({ ...assetTag, ...orgTags.find((orgTag) => orgTag._id === assetTag._id) }));
    }
    dispatch({
      type: ActionType.UpdateCurrentAssetTags,
      payload: {
        currentAssetTags,
      },
    });
    assetId.forEach(async (a) => {
      switch (type) {
        case UpdateSearchHitType.AddTag:
          updateSearchHitAddTag(a, tag)(dispatch, getState);
          break;
        case UpdateSearchHitType.RemoveTag:
          updateSearchHitRemoveTag(a, tag)(dispatch, getState);
          break;
        case UpdateSearchHitType.EditTag:
          updateSearchHitTags(a, currentAssetTags)(dispatch, getState);
          break;
        case UpdateSearchHitType.DeleteTag:
          updateSearchHitTags(a, currentAssetTags)(dispatch, getState);
          break;
        default:
          break;
      }
    });
  } catch (e) {
    console.error('[QA] could not update current asset tags', e.toString());
  }
};

export const updateIndeterminateTags = (assetId: string[], indeterminateTags: ITag[]) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const state = getState();
    const orgTags = [...state.tags.orgTags];
    const tags = [...indeterminateTags];
    let currentIndeterminateTags: ITag[];
    if (indeterminateTags.length > 0) {
      currentIndeterminateTags = tags.map((indeterminateTag) => ({ ...indeterminateTag, ...orgTags.find((orgTag) => orgTag._id === indeterminateTag._id) }));
    }
    dispatch({
      type: ActionType.UpdateCurrentIndeterminateTags,
      payload: {
        currentIndeterminateTags,
      },
    });
  } catch (e) {
    console.error('[QA] could not update current indeterminate tags', e.toString());
  }
};

export const updateAssetTotals = (params: ITag[]) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    dispatch({ type: ActionType.OrgTagsLoading });
    const state = getState();
    params.forEach(async (alteredtag) => {
      const orgTags = [...state.tags.orgTags];
      if (orgTags.length > 0) {
        const tagIndex = orgTags.findIndex(((tag) => tag._id === alteredtag._id));
        orgTags[tagIndex].totalAssets = alteredtag.totalAssets;
      }
      dispatch({
        type: ActionType.UpdateTotalAssets,
        payload: {
          orgTags: [...orgTags],
        },
      });
    });
  } catch (e) {
    console.error('[QA] could not update current asset tags totals', e.toString());
  }
};

export const addTag = (params: ITagCreate) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const state = getState();
    const userOrgIds = selectUserOrgIds(state);
    const orgId = userOrgIds[0];

    dispatch({
      type: ActionType.AddTag,
    });

    const createResponse = await createTag(params);

    if (userOrgIds.length > 1) {
      console.warn(`[QA] User belongs to multiple orgs -> targeting "${orgId}"`);
    }

    const response = await getTagsByOrgId(orgId);
    const tags = response.data.data;

    dispatch({
      type: ActionType.OrgTagsLoaded,
      payload: {
        tags,
      },
    });
  } catch (e) {
    console.error('[QA] could not load tag data', e.toString());
  }
};

export const refreshTags = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const state = getState();
    await loadOrgTags()(dispatch, getState);
    const orgTags = [...state.tags.orgTags];
    const currentAssetTags = [...state.tags.currentAssetTags];
    let updatedTags: ITag[];
    if (currentAssetTags.length > 0) {
      updatedTags = currentAssetTags.map((assetTag) => ({ ...assetTag, ...orgTags.find((orgTag) => orgTag._id === assetTag._id) }));
    }
    dispatch({
      type: ActionType.UpdateCurrentAssetTags,
      payload: {
        currentAssetTags: updatedTags,
      },
    });
  } catch (e) {
    console.error('[QA] could not tag asset', e.toString());
  }
};

export const editTag = (id: string, params: ITagPut) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const state = getState();
    const userOrgIds = selectUserOrgIds(state);
    const orgId = userOrgIds[0];

    dispatch({ type: ActionType.UpdateTag });

    const updateResponse = await updateTag(id, params);

    if (userOrgIds.length > 1) {
      console.warn(`[QA] User belongs to multiple orgs -> targeting "${orgId}"`);
    }

    await refreshTags()(dispatch, getState);
    await updateTagList(UpdateSearchHitType.EditTag)(dispatch, getState);

    const response = await getTagsByOrgId(orgId);
    const tags = response.data.data;

    dispatch({
      type: ActionType.OrgTagsLoaded,
      payload: {
        tags,
      },
    });
  } catch (e) {
    console.error('[QA] could not load tag data', e.toString());
  }
};

export const updateTagList = (type: UpdateSearchHitType) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const state = getState();
    await queryGamesAction()(dispatch, getState);
    await updateAssetTags(type, [state.tags.currentAssetId], state.tags.currentAssetTags)(dispatch, getState);
  } catch (e) {
    console.error('[QA] could not load tag data', e.toString());
  }
};

export const removeTag = (id: string) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const state = getState();
    const userOrgIds = selectUserOrgIds(state);
    const orgId = userOrgIds[0];

    dispatch({ type: ActionType.DeleteTag });

    const deleteResponse = await deleteTag(id);

    if (userOrgIds.length > 1) {
      console.warn(`[QA] User belongs to multiple orgs -> targeting "${orgId}"`);
    }

    await refreshTags()(dispatch, getState);
    await updateTagList(UpdateSearchHitType.DeleteTag)(dispatch, getState);
  } catch (e) {
    console.error('[QA] could not load tag data', e.toString());
  }
};

export const tagAsset = (assetIds: string[], tag: ITag, assetTags: ITag[], indeterminateTags?: ITag[]) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const params: ITagAsset = {
      assetIds,
      tagIds: [tag._id],
    };
    updateAssetTags(UpdateSearchHitType.AddTag, assetIds, assetTags, tag)(dispatch, getState);
    if (indeterminateTags != null) {
      updateIndeterminateTags(params.assetIds, indeterminateTags)(dispatch, getState);
    }
    dispatch({
      type: ActionType.TagAssignedToAsset,
    });
    const tagResponse = await addTagToAsset(params);
    updateAssetTotals(tagResponse.data.data.calculatedTags)(dispatch, getState);
  } catch (e) {
    console.error('[QA] could not tag asset', e.toString());
  }
};

export const untagAsset = (assetIds: string[], tag: ITag, assetTags: ITag[]) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const params: ITagAsset = {
      assetIds,
      tagIds: [tag._id],
    };
    updateAssetTags(UpdateSearchHitType.RemoveTag, assetIds, assetTags, tag)(dispatch, getState);
    dispatch({
      type: ActionType.TagRemovedFromAsset,
    });
    const tagResponse = await removeTagFromAsset(params);
    updateAssetTotals(tagResponse.data.data.calculatedTags)(dispatch, getState);
  } catch (e) {
    console.error('[QA] could not tag asset', e.toString());
  }
};

export const setCurrentAssetId = (assetId: string) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    dispatch({
      type: ActionType.SetCurrentAssetId,
      payload: {
        assetId,
      },
    });
  } catch (e) {
    console.error('[QA] could not set assetId', e.toString());
  }
};

export const selectTagsByOrgId = (state: IAppState): Array<ITag> => {
  const orgTags = state.tags?.orgTags;
  if (orgTags?.length > 0) {
    const tagList = [...orgTags];
    return _.orderBy(tagList, [(u) => u.name.toLowerCase()], 'asc');
  }
  return [];
};

export const selectAllTags = (state: IAppState): Array<ITag> => {
  const adminTags = state.tags?.adminTags;
  if (adminTags?.length > 0) {
    const tagList = [...adminTags];
    return _.orderBy(tagList, [(u) => u.name.toLowerCase()], 'asc');
  }
  return [];
};

export const selectCurrentAssetId = (state: IAppState): string => state.tags.currentAssetId;

export const selectCurrentAssetTags = (state: IAppState): Array<ITag> => {
  if (state.tags !== undefined && state.tags.currentAssetTags !== undefined && state.tags.currentAssetTags.length > 0) {
    const tagList = [...state.tags.currentAssetTags];
    return tagList;
  }
  return [];
};

export const selectCurrentIndeterminateTags = (state: IAppState): Array<ITag> => {
  if (state.tags !== undefined && state.tags.currentIndeterminateTags !== undefined && state.tags.currentIndeterminateTags.length > 0) {
    const tagList = [...state.tags.currentIndeterminateTags];
    return tagList;
  }
  return [];
};

export const selectOrgTagsLoaded = (state: IAppState): boolean => (state.tags.orgTagsLoaded);

export const selectCurrentAssetTagsLoaded = (state: IAppState): boolean => (state.tags.currentAssetTagsLoaded);

export const selectIsAdminTagsLoaded = (state: IAppState): boolean => (state.tags.isAdminTagsLoaded);
